import React, { useState, useRef, useEffect, type Ref, useMemo, useImperativeHandle, useCallback } from 'react';
import { Box, Grid, Typography, Button, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { templateService } from '@/services';
import type { MediaPlayerTemplate } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import FormContainer, { FormContainerRef } from '@/components/form/FormContainer.tsx';
import { validateName } from '@core/utils/validation.ts';
import FormTextField from '@/components/form/FormTextField.tsx';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';
import { useSettingsStore } from '@/core/store';
import { TemplateVariable } from '@/core/types';
import { WcnButton } from '@/components';

export interface MediaPlayerTemplatesEditorRef {
    isDirty: boolean;
    save: () => Promise<boolean>;
}

interface MediaPlayerTemplatesEditorProps {
    template: MediaPlayerTemplate;
    onSave: () => void;
    onCancel: () => void;
    onDelete: (template: MediaPlayerTemplate) => void;
    ref?: Ref<MediaPlayerTemplatesEditorRef>;
}

const TEMPLATE_VARIABLES: TemplateVariable[] = [{ name: 'shopDisplayName', label: 'shopDisplayName' }];

const MediaPlayerTemplatesEditor: React.FC<MediaPlayerTemplatesEditorProps> = ({
    template: initialTemplate,
    onSave,
    onCancel,
    onDelete,
    ref
}) => {
    const { t } = useTranslation();
    const { notifyErrorResponse, notifySuccess, notifyWarning } = useNotifications();
    const { isMobile } = useSettingsStore();

    const [currentFocusedField, setCurrentFocusedField] = useState<{ id: string; field: string } | null>(null);
    const [template, setTemplate] = useState<MediaPlayerTemplate>(initialTemplate);
    const formRef = useRef<FormContainerRef>(null);
    const [formValid, setFormValid] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setTemplate(initialTemplate);
        setFormValid(true);
        formRef.current?.resetValidation();
    }, [initialTemplate]);

    const isDirty = useMemo(() => {
        return (
            template.name !== initialTemplate.name ||
            JSON.stringify(template.configuration) !== JSON.stringify(initialTemplate.configuration)
        );
    }, [template, initialTemplate]);

    const handleFieldChange = (field: string, value: string) => {
        setTemplate((prev) => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                [field]: value
            }
        }));
    };

    const handleNameChange = (value: string) => {
        setTemplate((prev) => ({
            ...prev,
            name: value
        }));
    };

    const performSave = useCallback(async (): Promise<boolean> => {
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            notifyWarning(formRef.current?.getRequiredWarning() || 'required');
            return false;
        }

        setLoading(true);
        try {
            const isNew = !template.id;
            if (isNew) {
                await templateService.saveMediaPlayerTemplate(
                    (({ id, ...rest }) => rest)(template) as MediaPlayerTemplate
                );
            } else {
                await templateService.saveMediaPlayerTemplate(template);
            }
            notifySuccess('configurationSaved');
            return true;
        } catch (error) {
            notifyErrorResponse(error);
            return false;
        } finally {
            setLoading(false);
        }
    }, [formValid, template, t]);

    useImperativeHandle(ref, () => ({ isDirty, save: performSave }), [isDirty, performSave]);

    const handleSave = useCallback(async () => {
        const saved = await performSave();
        if (saved) onSave();
    }, [performSave]);

    const handleDelete = () => {
        onDelete(template);
    };

    const handleInsertVariable = (variableName: string) => {
        if (!currentFocusedField) return;
        const inputElement = document.getElementById(currentFocusedField.id) as HTMLInputElement;
        if (!inputElement) return;
        const startPosition = inputElement.selectionStart || 0;
        const endPosition = inputElement.selectionEnd || 0;

        const wrappedVariable = `{${variableName}}`;

        // Assign value directly to DOM element, or else React might override it on the next render.
        inputElement.value = `${inputElement.value.substring(
            0,
            startPosition
        )}${wrappedVariable}${inputElement.value.substring(endPosition, inputElement.value.length)}`;

        handleFieldChange(currentFocusedField.field, inputElement.value);

        // Move the cursor to the end of the inserted variable
        const newCursorPosition = startPosition + wrappedVariable.length;
        inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
        inputElement.focus();
    };

    const mediaPlayerRule = (value: string): string => {
        if (!value) return '';
        return value.includes('!') || value.includes(':') ? t('forbiddenSpecialCharacters') : '';
    };

    const maxCharactorRule = (value: string): string => {
        if (!value) return '';
        return value.length > 20 ? t('validation.inputMaxLength', { length: 20 }) : '';
    };

    return (
        <Paper variant="outlined" sx={{ borderRadius: '3px', overflow: 'visible' }}>
            {/* Section header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: isMobile ? 1.25 : 3,
                    py: isMobile ? 1.25 : 3
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                    {template.id ? t('editMediaPlayerConfiguration') : t('addMediaPlayerConfiguration')}
                </Typography>
            </Box>

            <Box sx={{ padding: isMobile ? '24px 10px' : '80px 30px 10px 24px' }}>
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <Grid container gap={isMobile ? 0 : 4}>
                        <Grid container item spacing={2}>
                            <Grid item xs={12} sm={2}>
                                <Typography
                                    sx={{
                                        mt: isMobile ? 0 : 2,
                                        color: 'var(--gray-medium)',
                                        fontSize: '12px',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    {t('template')}
                                </Typography>
                            </Grid>
                            <Grid container item xs={12} sm={10} spacing={4}>
                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'name'}
                                        label={t('name')}
                                        sx={{ maxWidth: '368px' }}
                                        value={template.name}
                                        onChange={(e) => handleNameChange(e.target.value)}
                                        disabled={!!template.id}
                                        required
                                        rules={[validateName(t)]}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography
                                        variant="body2"
                                        sx={{ pl: 1, mb: 1, color: 'var(--gray-medium)', fontSize: '12px' }}
                                    >
                                        {t('insertVariables')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 2.5 }}>
                                        {TEMPLATE_VARIABLES.map((variable) => (
                                            <Button
                                                key={variable.name}
                                                variant="text"
                                                startIcon={<i className="bi bi-code-square" />}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => handleInsertVariable(variable.name)}
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    borderRadius: 0,
                                                    color: 'black',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                {t(variable.label)}
                                            </Button>
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container item spacing={2}>
                            <Grid item sm={2} xs={12}>
                                <Typography
                                    sx={{
                                        mt: isMobile ? 0 : 2,
                                        color: 'var(--gray-medium)',
                                        fontSize: '12px',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    {t('individualText')}
                                </Typography>
                            </Grid>
                            <Grid container item sm={8} xs={12} spacing={4}>
                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'defaultWelcome'}
                                        id="defaultWelcomeId"
                                        label={t('defaultWelcome')}
                                        value={template.configuration.defaultWelcome}
                                        onChange={(e) => handleFieldChange('defaultWelcome', e.target.value)}
                                        rules={[(value) => mediaPlayerRule(value)]}
                                        onFocus={() =>
                                            setCurrentFocusedField({ id: 'defaultWelcomeId', field: 'defaultWelcome' })
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'defaultTitle'}
                                        id="defaultTitleId"
                                        label={t('defaultTitle')}
                                        value={template.configuration.defaultTitle}
                                        onChange={(e) => handleFieldChange('defaultTitle', e.target.value)}
                                        rules={[(value) => mediaPlayerRule(value)]}
                                        onFocus={() =>
                                            setCurrentFocusedField({ id: 'defaultTitleId', field: 'defaultTitle' })
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'defaultFirstName'}
                                        id="defaultFirstNameId"
                                        label={t('defaultFirstName')}
                                        value={template.configuration.defaultFirstName}
                                        onChange={(e) => handleFieldChange('defaultFirstName', e.target.value)}
                                        rules={[(value) => mediaPlayerRule(value)]}
                                        onFocus={() =>
                                            setCurrentFocusedField({
                                                id: 'defaultFirstNameId',
                                                field: 'defaultFirstName'
                                            })
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'defaultLastName'}
                                        id="defaultLastNameId"
                                        label={t('defaultLastName')}
                                        value={template.configuration.defaultLastName}
                                        onChange={(e) => handleFieldChange('defaultLastName', e.target.value)}
                                        rules={[(value) => mediaPlayerRule(value)]}
                                        onFocus={() =>
                                            setCurrentFocusedField({
                                                id: 'defaultLastNameId',
                                                field: 'defaultLastName'
                                            })
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'alternateLine1'}
                                        id="alternateLine1Id"
                                        label={t('alternateLine1')}
                                        value={template.configuration.alternateLine1}
                                        onChange={(e) => handleFieldChange('alternateLine1', e.target.value)}
                                        rules={[mediaPlayerRule, maxCharactorRule]}
                                        onFocus={() =>
                                            setCurrentFocusedField({ id: 'alternateLine1Id', field: 'alternateLine1' })
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'alternateLine2'}
                                        id="alternateLine2Id"
                                        label={t('alternateLine2')}
                                        value={template.configuration.alternateLine2}
                                        onChange={(e) => handleFieldChange('alternateLine2', e.target.value)}
                                        rules={[mediaPlayerRule, maxCharactorRule]}
                                        onFocus={() =>
                                            setCurrentFocusedField({ id: 'alternateLine2Id', field: 'alternateLine2' })
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'alternateLine3'}
                                        id="alternateLine3Id"
                                        label={t('alternateLine3')}
                                        value={template.configuration.alternateLine3}
                                        onChange={(e) => handleFieldChange('alternateLine3', e.target.value)}
                                        rules={[mediaPlayerRule, maxCharactorRule]}
                                        onFocus={() =>
                                            setCurrentFocusedField({ id: 'alternateLine3Id', field: 'alternateLine3' })
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'alternateLine4'}
                                        id="alternateLine4Id"
                                        label={t('alternateLine4')}
                                        value={template.configuration.alternateLine4}
                                        onChange={(e) => handleFieldChange('alternateLine4', e.target.value)}
                                        rules={[mediaPlayerRule, maxCharactorRule]}
                                        onFocus={() =>
                                            setCurrentFocusedField({ id: 'alternateLine4Id', field: 'alternateLine4' })
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </FormContainer>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: isMobile ? 'center' : 'flex-end',
                        alignItems: isMobile ? 'center' : 'unset',
                        gap: 2.25,
                        mt: 7,
                        mb: 3
                    }}
                >
                    {isMobile ? (
                        <>
                            <WcnButton
                                variant="primary"
                                onClick={handleSave}
                                disabled={loading || !formValid}
                                style={{ width: '270px' }}
                            >
                                {t('save')}
                            </WcnButton>
                            <WcnButton variant="red" onClick={onCancel} style={{ width: '270px' }}>
                                {t('cancel')}
                            </WcnButton>
                            {template.id && (
                                <WcnButton variant="red" onClick={handleDelete} style={{ width: '270px' }}>
                                    {t('delete')}
                                </WcnButton>
                            )}
                        </>
                    ) : (
                        <>
                            {template.id && (
                                <WcnButton variant="red" onClick={handleDelete}>
                                    {t('delete')}
                                </WcnButton>
                            )}
                            <WcnButton variant="red" onClick={onCancel}>
                                {t('cancel')}
                            </WcnButton>
                            <WcnButton variant="primary" onClick={handleSave} disabled={loading || !formValid}>
                                {t('save')}
                            </WcnButton>
                        </>
                    )}
                </Box>

                <LoadingOverlay open={loading} />
            </Box>
        </Paper>
    );
};

export default MediaPlayerTemplatesEditor;
